import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import type { MentionType } from './MentionTypes';

interface MentionChipProps {
  type: MentionType | 'auto';
  value: string;
  showRemoveButton?: boolean;
  onRemove?: () => void;
  className?: string;
}

// Mapeo de tipos a etiquetas legibles
const TYPE_LABELS: Record<MentionType | 'auto', string> = {
  empresa: 'empresa',
  tipo: 'tipo',
  fecha: 'fecha',
  sector: 'sector',
  documento: 'documento',
  auto: 'empresa',
};

// Mapeo de tipos a iconos
const TYPE_ICONS: Record<MentionType | 'auto', string> = {
  empresa: '🏢',
  tipo: '📄',
  fecha: '📅',
  sector: '🏭',
  documento: '📄',
  auto: '🏢',
};

/**
 * Componente de chip para mostrar una mención/filtro
 * Reutilizable en el input de chat y en los mensajes del usuario
 */
const MentionChip: React.FC<MentionChipProps> = ({
  type,
  value,
  showRemoveButton = false,
  onRemove,
  className = '',
}) => {
  const { theme } = useTheme();
  const isDark = theme.mode === 'dark';

  const typeLabel = TYPE_LABELS[type] || 'filtro';
  const icon = TYPE_ICONS[type] || '🏢';

  // Para documentos, mostrar solo el nombre completo sin el prefijo "documento:"
  // Para otros tipos, mostrar "tipo: valor"
  const chipText = type === 'documento' ? value : `${typeLabel}: ${value}`;

  // Estilos específicos por tipo (igual que FormattedTextInput)
  const getChipStyle = () => {
    if (type === 'documento') {
      return {
        backgroundColor: isDark ? 'rgba(139, 142, 230, 0.15)' : 'rgba(139, 142, 230, 0.08)',
        color: isDark ? '#a5b4fc' : '#818cf8',
        border: `1px solid ${isDark ? 'rgba(139, 142, 230, 0.3)' : 'rgba(139, 142, 230, 0.2)'}`,
      };
    }
    // Estilo por defecto para otros tipos
    return {
      backgroundColor: isDark ? 'rgba(55, 65, 81, 0.8)' : 'rgba(229, 231, 235, 0.8)',
      color: isDark ? '#e5e7eb' : '#374151',
      border: 'none',
    };
  };

  const chipStyle = getChipStyle();

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-normal align-middle select-none ${className}`}
      style={{
        ...chipStyle,
        verticalAlign: 'middle',
        margin: '0 2px',
      }}
    >
      <span style={{ fontSize: '14px', lineHeight: 1, opacity: 0.8 }}>{icon}</span>
      {/* Para documentos, mostrar el prefijo "@" visible como en FormattedTextInput */}
      {type === 'documento' && <span style={{ fontWeight: 500, marginRight: '2px' }}>@</span>}
      <span style={{ whiteSpace: 'nowrap' }}>{chipText}</span>
      {showRemoveButton && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="inline-flex items-center justify-center ml-0.5 rounded transition-opacity hover:opacity-100"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1,
            width: '16px',
            height: '16px',
            minWidth: '16px',
            minHeight: '16px',
            opacity: 0.8,
            outline: 'none',
          }}
          aria-label="Eliminar filtro"
          tabIndex={-1}
        >
          ×
        </button>
      )}
    </span>
  );
};

export default MentionChip;
